/*
 * Copyright (c) 2026. Orion Analytics. All Rights Reserved
 */

import Plot from "react-plotly.js";

export default function OIBuildUpDashboard({
    futuresData = [],
    optionsData = []
    }) {

    const allSignals = [
        ...futuresData,
        ...optionsData
    ];

    const totalOI =
        allSignals.reduce(
            (sum, row) =>
                sum + (row.oi || 0),
            0
        );

    const bullishSignals =
        allSignals.filter(
            d =>
                d.signal === "Long Build-up" ||
                d.signal === "Short Covering" ||
                d.signal === "Put Writing"
        ).length;

    const bearishSignals =
        allSignals.filter(
            d =>
                d.signal === "Short Build-up" ||
                d.signal === "Long Unwinding" ||
                d.signal === "Call Writing"
        ).length;

    const longBuildUp =
        allSignals.filter(
            d =>
                d.signal ===
                "Long Build-up"
        ).length;

    const shortBuildUp =
        allSignals.filter(
            d =>
                d.signal ===
                "Short Build-up"
        ).length;

    const callWriting =
        allSignals.filter(
            d =>
                d.signal ===
                "Call Writing"
        ).length;

    const putWriting =
        allSignals.filter(
            d =>
                d.signal ===
                "Put Writing"
        ).length;

    const signalCounts = {};

    allSignals.forEach(row => {

        const signal =
            row.signal || "Unknown";

        signalCounts[signal] =
            (signalCounts[signal] || 0) + 1;

    });

    const signalNames =
        Object.keys(signalCounts);

    const signalValues =
        Object.values(signalCounts);

    const StatCard = ({
        title,
        value,
        color
    }) => (

        <div className="
            bg-slate-950
            border
            border-slate-800
            rounded-3xl
            p-5
        ">

            <div className="
                text-slate-400
                text-sm
                mb-2
            ">
                {title}
            </div>

            <div
                className="
                    text-3xl
                    font-bold
                "
                style={{
                    color
                }}
            >
                {value}
            </div>

        </div>

    );

    return (

        <div className="
            space-y-6
        ">

            {/* KPI CARDS */}

            <div className="
                grid
                grid-cols-1
                sm:grid-cols-2
                lg:grid-cols-3
                xl:grid-cols-6
                gap-4
            ">

                <StatCard
                    title="Total OI"
                    value={
                        totalOI.toLocaleString()
                    }
                    color="#38bdf8"
                />

                <StatCard
                    title="Bullish Signals"
                    value={bullishSignals}
                    color="#22c55e"
                />

                <StatCard
                    title="Bearish Signals"
                    value={bearishSignals}
                    color="#ef4444"
                />

                <StatCard
                    title="Long Build-Up"
                    value={longBuildUp}
                    color="#10b981"
                />

                <StatCard
                    title="Short Build-Up"
                    value={shortBuildUp}
                    color="#f97316"
                />

                <StatCard
                    title="Put Writing"
                    value={putWriting}
                    color="#8b5cf6"
                />

            </div>

            {/* SECOND ROW */}

            <div className="
                grid
                grid-cols-1
                xl:grid-cols-3
                gap-6
            ">

                {/* SIGNAL DISTRIBUTION */}

                <div className="
                    xl:col-span-2
                    bg-slate-950
                    border
                    border-slate-800
                    rounded-3xl
                    p-4
                ">

                    <h3 className="
                        text-white
                        font-semibold
                        mb-4
                    ">
                        OI Signal Distribution
                    </h3>

                    <Plot
                        data={[
                            {
                                x: signalNames,

                                y: signalValues,

                                type: "bar",

                                marker: {
                                    color: [
                                        "#22c55e",
                                        "#ef4444",
                                        "#3b82f6",
                                        "#f59e0b",
                                        "#8b5cf6",
                                        "#06b6d4"
                                    ]
                                }
                            }
                        ]}
                        layout={{
                            height: 350,

                            paper_bgcolor:
                                "#020617",

                            plot_bgcolor:
                                "#020617",

                            font: {
                                color:
                                    "#cbd5e1"
                            },

                            margin: {
                                l: 40,
                                r: 20,
                                t: 20,
                                b: 80
                            },

                            xaxis: {
                                tickangle: -30
                            },

                            yaxis: {
                                title:
                                    "Count",

                                gridcolor:
                                    "#1e293b"
                            }
                        }}
                        config={{
                            displayModeBar:
                                false,
                            responsive: true
                        }}
                        style={{
                            width: "100%"
                        }}
                    />

                </div>

                {/* SENTIMENT GAUGE */}

                <div className="
                    bg-slate-950
                    border
                    border-slate-800
                    rounded-3xl
                    p-4
                    flex
                    flex-col
                    justify-center
                ">

                    <h3 className="
                        text-white
                        font-semibold
                        mb-6
                        text-center
                    ">
                        OI Sentiment
                    </h3>

                    <div className="
                        flex
                        justify-center
                    ">

                        <div className="
                            relative
                            w-48
                            h-48
                            rounded-full
                            border-8
                            border-slate-700
                            flex
                            items-center
                            justify-center
                        ">

                            <div className="
                                text-center
                            ">

                                <div className="
                                    text-4xl
                                    font-bold
                                    text-white
                                ">
                                    {
                                        bullishSignals >
                                        bearishSignals
                                            ? "🟢"
                                            : bearishSignals >
                                              bullishSignals
                                            ? "🔴"
                                            : "⚪"
                                    }
                                </div>

                                <div className="
                                    text-slate-300
                                    mt-2
                                ">
                                    {
                                        bullishSignals >
                                        bearishSignals
                                            ? "Bullish"
                                            : bearishSignals >
                                              bullishSignals
                                            ? "Bearish"
                                            : "Neutral"
                                    }
                                </div>

                            </div>

                        </div>

                    </div>

                    <div className="
                        mt-6
                        text-center
                        text-slate-400
                        text-sm
                    ">
                        Bullish vs Bearish
                        positioning derived
                        from OI build-up
                        signals.
                    </div>

                </div>

            </div>

        </div>

    );


}
